/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import { useCallback } from "react";
import { Note } from "@/lib/api/notes.ts";
import { UseNoteForm } from "@/lib/hooks/conversations/use-note-form.tsx";
import { Time } from "@internationalized/date";
import { TimeValue } from "react-aria";
import { DateTime } from "luxon";
import { DEFAULT_TIMEZONE } from "@/lib/constants";

export function useNoteUtils() {
  const resetNoteForm = useCallback((form: UseNoteForm) => {
    form.reset({
      time: undefined,
      date: undefined,
      timezone: "",
      reminderVisible: false,
      metadata: {},
      content: "",
      title: "",
    });
  }, []);

  const convertFormValuesToReminder = useCallback(
    (
      date: Date | undefined,
      time: TimeValue | undefined,
      timezone: string | undefined,
    ) => {
      if (!date || !time || !timezone) return undefined;

      // Create a DateTime object in the selected timezone
      // This interprets the date/time as being in that timezone
      const dt = DateTime.fromObject(
        {
          year: date.getFullYear(),
          month: date.getMonth() + 1, // Luxon months are 1-indexed
          day: date.getDate(),
          hour: time.hour,
          minute: time.minute,
          second: 0,
          millisecond: 0,
        },
        { zone: timezone },
      );

      // Return ISO string with timezone offset
      return dt.toISO();
    },
    [],
  );

  const convertReminderToFormValues = useCallback(
    (reminder?: string, reminderTimezone?: string) => {
      if (!reminder) return undefined;

      // Fall back to DEFAULT_TIMEZONE if timezone is missing or empty
      // This handles legacy reminders that don't have timezone metadata
      const timezone = reminderTimezone || DEFAULT_TIMEZONE;

      // Parse the ISO timestamp and convert to the stored timezone
      const dt = DateTime.fromISO(reminder, { zone: timezone });

      // Create a Date object for the date picker (will be in local time, but that's ok)
      // The date picker just needs a date object to display the correct calendar date
      const date = new Date(dt.year, dt.month - 1, dt.day);

      // Create Time object with the hour/minute from the stored timezone
      const time = new Time(dt.hour, dt.minute);

      return {
        date,
        time,
        timezone,
      };
    },
    [],
  );

  const setNoteFormValues = useCallback((form: UseNoteForm, note: Note) => {
    form.reset({
      title: note.title,
      content: note.content,
      metadata: note.metadata || {},
      reminderVisible: !!note.reminder,
      ...(note.reminder &&
        convertReminderToFormValues(
          note.reminder,
          (note.metadata?.reminderTimezone as string) || "",
        )),
    });
  }, []);

  const isNotePinned = useCallback((note: Note) => {
    if (
      !note.metadata?.pinnedByMentor ||
      typeof note.metadata?.pinnedByMentor !== "object"
    )
      return false;

    return "value" in note.metadata.pinnedByMentor
      ? (note.metadata?.pinnedByMentor.value as boolean)
      : false;
  }, []);

  return {
    isNotePinned,
    resetNoteForm,
    setNoteFormValues,
    convertFormValuesToReminder,
    convertReminderToFormValues,
  };
}
